import { SkillMetadata } from "@models";
import { MenuItem, TextField, Tooltip } from "@mui/material";
import React, { useEffect, useRef } from "react";

type MenuSkillsContentProps = {
  isMenuOpen: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredSkills: SkillMetadata[];
  attachedSkills: SkillMetadata[];
  handleSkillSelect: (skill: SkillMetadata) => void;
};

export const MenuSkillsContent: React.FC<MenuSkillsContentProps> = ({
  isMenuOpen,
  searchQuery,
  setSearchQuery,
  filteredSkills,
  attachedSkills,
  handleSkillSelect,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);
  const activeIndexRef = useRef(-1);

  useEffect(() => {
    if (isMenuOpen && inputRef.current) {
      const focusTimer = window.setTimeout(() => {
        inputRef.current?.focus();
      }, 0);

      return () => {
        window.clearTimeout(focusTimer);
      };
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) {
      activeIndexRef.current = -1;
      itemRefs.current = [];
    }
  }, [isMenuOpen]);

  const attachedSkillIds = new Set(attachedSkills.map((skill) => skill.id));
  const isAttachableSkill = (skill: SkillMetadata) =>
    !attachedSkillIds.has(skill.id) && skill.enabled && !skill.forceEnabled;

  const sortedSkills = [...filteredSkills]
    .filter(isAttachableSkill)
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    });

  const moveFocus = (nextIndex: number) => {
    if (sortedSkills.length === 0) {
      return;
    }
    let target = nextIndex;
    if (target < 0) {
      target = sortedSkills.length - 1;
    }
    if (target >= sortedSkills.length) {
      target = 0;
    }
    activeIndexRef.current = target;
    itemRefs.current[target]?.focus();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus(0);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(sortedSkills.length - 1);
    }
  };

  const handleItemKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus(index + 1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(index - 1);
    }
  };

  return (
    <div
      data-testid="menu-skills-content"
      style={{
        display: "flex",
        flex: "1 1 0",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        className="border-b border-zinc-200 p-3"
        style={{
          flex: "0 0 auto",
          marginLeft: 6,
          marginRight: 6,
        }}
      >
        <TextField
          size="small"
          placeholder="Search skills..."
          value={searchQuery}
          onChange={(e) => {
            activeIndexRef.current = -1;
            setSearchQuery(e.target.value);
          }}
          onKeyDown={handleInputKeyDown}
          fullWidth
          variant="outlined"
          inputRef={inputRef}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "0.875rem",
            },

            "& .MuiOutlinedInput-notchedOutline": {
              top: "-10px",
            },
          }}
        />
      </div>
      <div
        data-testid="menu-skills-scroll-region"
        style={{
          flex: "1 1 0",
          minHeight: 0,
          overflowY: "auto",
          position: "relative",
        }}
      >
        {sortedSkills.length === 0 && (
          <MenuItem disabled>
            <span className="text-sm text-zinc-500">
              {searchQuery ? "No skills found" : "No skills available"}
            </span>
          </MenuItem>
        )}

        {sortedSkills.map((skill, itemIndex) => (
          <Tooltip
            key={skill.id}
            title={skill.descriptionMd}
            placement="right"
            describeChild
          >
            <MenuItem
              onClick={() => handleSkillSelect(skill)}
              onKeyDown={(event) => {
                handleItemKeyDown(event, itemIndex);
              }}
              ref={(element) => {
                itemRefs.current[itemIndex] = element;
              }}
              sx={{
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              style={{ margin: "4px 6px" }}
              className="gap-3"
            >
              <span
                className="text-sm font-medium min-w-0 flex-1 whitespace-normal break-words [overflow-wrap:anywhere]"
                style={
                  {
                    textWrap: "auto",
                  } as unknown as React.CSSProperties
                }
              >
                {skill.name}
              </span>
            </MenuItem>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};
